/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, useCallback, useMemo } from 'react';

import get from 'lodash/get';

import { FILTERS } from '../../../constants';
import useLazyCategoryId from '../../../hooks/use-lazy-category-id';
import { useStaticProps } from '../../context/static-props';
import { useSearch } from '../../../hooks/context';
import { trackZrp } from '../track';
import { useLastFilterId, setLastFilterId } from '../../context/filters';
import {
  deserializeItemLocation,
  getPathname,
  serializeItemLocation,
  removeItemLocation,
} from '../../context/filters/helpers';
import getHistory from '../../../lib/history';
import { setSnackbar } from '../../context/snackbar';
import { POLYGON_EVENTS } from '../lib';
import useMapConfig from './use-map-config';

const { ITEM_LOCATION } = FILTERS.IDS;
const { IDLE } = POLYGON_EVENTS;

const INITIAL_BOUNDS = { north: null, south: null, east: null, west: null };

const disableScrollRestoration = (history) => {
  if (history.scrollRestoration) {
    history.scrollRestoration = 'manual';
  }
};

const getAllAppliedFilters = ({
  filtering_options: filteringOptions = {},
  map_configuration: mapConfiguration = {},
  new_faceted_search: newFacetedSearch = {},
}) =>
  newFacetedSearch.applied_filters ||
  filteringOptions.applied_filters ||
  get(mapConfiguration, 'filters.new_faceted_search.applied_filters', null) ||
  get(mapConfiguration, 'filters.applied_filters.filters', []);

/**
 * Custom hook that manages map view state and operations
 *
 * This hook provides comprehensive map functionality including:
 * - Results filtering and pagination
 * - Error handling with automatic snackbar notifications
 * - Polygon toggler state management (when applicable)
 * - Location bounds management and navigation
 * - Zero results page (ZRP) display logic
 *
 * The hook automatically handles map errors by displaying snackbars with retry
 * functionality, managing polygon toggler state reset, and providing consistent
 * error UX across desktop and mobile platforms.
 *
 * @param {Object} params - Hook parameters
 * @param {Array} params.results - Array of search results to display on map
 * @param {Object} params.mapRef - React ref to map component (optional, for polygon state reset)
 * @returns {Object} Map view state and handlers
 * @returns {Function} returns.applyNewFilters - Apply new filters to search results
 * @returns {boolean} returns.isLoading - Whether map is in loading state
 * @returns {boolean} returns.fitMarkersInMap - Whether to fit all markers in view
 * @returns {Function} returns.updateResults - Update results with new bounds
 * @returns {Function} returns.reloadResults - Reload current results (error recovery)
 * @returns {Object} returns.map_configuration - Map configuration from search context
 * @returns {Object} returns.fixedCoords - Fixed coordinates for map positioning
 * @returns {boolean} returns.showZrp - Whether to show zero results page
 * @returns {boolean} returns.showError - Whether an error occurred
 * @returns {Object} returns.error - Current error object (if any)
 * @returns {Function} returns.setError - Function to set error state
 */
const useMapView = ({ results, mapRef }) => {
  const { siteId, visualId } = useStaticProps();
  const { getCategoryId } = useLazyCategoryId(siteId);
  const lastFilterId = useLastFilterId();
  const history = getHistory();
  const { error: errorConfig } = useMapConfig();

  const searchContext = useSearch();
  const {
    analytics_track: analytics,
    isLoadingResults,
    locationSearchError,
    map_configuration = {},
    view_options: { sort: { sorts } } = {},
  } = searchContext;
  const [bounds, setBounds] = useState(INITIAL_BOUNDS);
  const [error, setError] = useState(null);
  const isRequestPending = bounds !== INITIAL_BOUNDS;
  const isLoading = isRequestPending || isLoadingResults;
  const showZrp = !isLoading && results.length === 0;
  const showError = !isLoading && (!!error || !!locationSearchError);
  const fitMarkersInMap = results.length > 0 && lastFilterId !== ITEM_LOCATION;
  const appliedFilters = getAllAppliedFilters(searchContext);

  // We do not store the item_location filter value in the `bounds` variable since
  // it can lead to stale data because other parts of the app might remove said
  // filter, so we derive the value from the applied_filters array which will always
  // be up to date.
  const fixedCoords = useMemo(
    () =>
      deserializeItemLocation(
        get(
          appliedFilters.find(({ id }) => id === ITEM_LOCATION),
          'values[0].id',
        ),
      ),
    [appliedFilters],
  );

  const getNextPathname = (category, overrides = {}) =>
    getPathname({
      appliedFilters,
      sortingOptions: sorts,
      removeOffset: get(map_configuration, 'pagination.selected_page', 1) > 1,
      DisplayType: 'M',
      category,
      siteId,
      visualId,
      ...overrides,
    }).then((pathname) => pathname || history.location.pathname);

  const addItemLocation = (currentBounds) => (pathname) => `${pathname}${serializeItemLocation(currentBounds)}`;

  const buildNextLocation = (pathname) => ({
    pathname,
    search: history.location.search,
  });

  const updateResults = useCallback((currentBounds) => {
    setBounds(currentBounds);
    setLastFilterId(ITEM_LOCATION);
    setError(null);
  }, []);

  const reloadResults = (event) => {
    event.preventDefault();

    const { pathname, search } = history.location;
    const { location: { protocol, host } = {} } = window || {};
    const url = `${protocol}//${host}${pathname}${search}`;

    window.location = url;
  };

  const getNewResults = () =>
    getCategoryId()
      .then(getNextPathname)
      .then(removeItemLocation)
      .then(addItemLocation(bounds))
      .then(buildNextLocation)
      .then(history.push)
      .catch(setError);

  const applyNewFilters = (newFilters) =>
    getCategoryId()
      .then((category) => getNextPathname(category, { removeOffset: true, useExistingFilters: true, ...newFilters }))
      .then(buildNextLocation)
      .then(history.push)
      .catch(setError);

  useEffect(() => {
    if (isRequestPending) {
      getNewResults()
        .then(() => setBounds(INITIAL_BOUNDS))
        .catch((err) => {
          throw new Error(err);
        });
    }
  }, [isRequestPending]);

  // Discard the local error if a request is made outside of this component's scope.
  useEffect(() => {
    if (isLoadingResults) {
      setError(null);
    }
  }, [isLoadingResults]);

  useEffect(() => {
    if (!results || results.length === 0) {
      trackZrp(analytics);
    }
  }, [results]);

  useEffect(() => {
    if (window) {
      disableScrollRestoration(window.history);
    }
  }, []);

  // Integrated error handling with snackbar display
  useEffect(() => {
    if (showError && error) {
      const errorMessage = errorConfig?.message;
      const errorAction = errorConfig?.action;

      // Reset polygon toggler state if available (desktop maps with polygon functionality)
      if (mapRef?.current?.setPolygonTogglerState) {
        mapRef.current.setPolygonTogglerState(IDLE);
      }

      // Clear error state to prevent duplicate snackbars
      setError(null);

      setSnackbar({
        message: errorMessage,
        color: 'red',
        action: {
          text: errorAction,
          onClick: reloadResults,
        },
        key: `map-error-${Date.now()}`,
      });

      const hideTimer = setTimeout(() => {
        setSnackbar(null);
      }, 3000);

      return () => {
        clearTimeout(hideTimer);
      };
    }

    return undefined;
  }, [showError, error, reloadResults, setError, mapRef, errorConfig]);

  return {
    applyNewFilters,
    isLoading,
    fitMarkersInMap,
    updateResults,
    reloadResults,
    map_configuration,
    fixedCoords,
    showZrp,
    showError,
    error,
    setError,
  };
};

export { useMapView };
